/* eslint-disable @typescript-eslint/no-explicit-any */
import styled from 'styled-components';
import UploadFileIcon from '@mui/icons-material/UploadFile';
// import DriveFolderUploadIcon from '@mui/icons-material/DriveFolderUpload';

import { DocumentLoader } from '@common/hooks/useDocument';
import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { List, ListItem, Stack, ToggleButton, ToggleButtonGroup, alpha } from '@mui/material';
import { Button } from '@common/Button';
import { Clear, DriveFolderUpload, UploadFile } from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { TelnyxBucket } from '@requests/buckets';

const HEADER_HEIGHT = 100;
const TOP_NAVBAR_HEIGHT = 61;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0.75rem;
  gap: 1rem;
  height: calc(100vh - ${HEADER_HEIGHT + TOP_NAVBAR_HEIGHT}px - 16px);
  overflow: hidden;
  color: ${({ theme }) => theme.text.standard};
`;

const FileUpload = styled.div``;

export const CustomCard = styled.div<{ isAllOpen?: boolean }>`
  display: flex;
  flex-direction: column;
  flex-grow: ${({ isAllOpen }) => (isAllOpen ? 1 : 'unset')};
  border: 1px solid ${({ theme }) => theme.line.light};
  border-radius: 8px;
  overflow: hidden;

  .actions {
    display: grid;
    grid-template-columns: 4fr 6fr;
    gap: 8px;
    padding: 16px;
    border-top: 1px solid ${({ theme }) => theme.line.light};

    button {
      white-space: nowrap;
    }

    @media (max-width: 1800px) {
      svg {
        display: none;
      }
    }
  }
`;

const Outline = styled.div<{ isDragActive?: boolean }>`
  display: flex;
  align-items: center;
  flex-direction: column;
  border: 2px dashed ${({ theme, isDragActive }) => (isDragActive ? theme.primary : theme.line.light)};
  border-radius: 6px;
  width: calc(100% - 2rem);
  padding: 2.5rem 1rem;
  padding-bottom: 3rem;
  gap: 4px;

  svg {
    font-size: 2.4ch;
    margin-bottom: 0.35rem;
    height: 25px;
    color: ${({ theme }) => theme.primary};
  }

  h4 {
    font-family: 'PPFormula';
    font-size: 14px;
    padding-top: 3px;
    font-weight: 600;
  }

  p {
    color: ${({ theme }) => alpha(theme.text.standard, 0.6)};
    font-size: 13px;
    font-family: 'Inter';
  }

  &:hover {
    cursor: pointer;
  }
`;

const ListContainer = styled.div<{ isAllOpen?: boolean; isPaddingBottom?: boolean }>`
  flex-grow: 1;
  overflow-y: auto;
  padding-top: 0.75rem;
  padding-bottom: ${({ isPaddingBottom }) => (!isPaddingBottom ? '0.75rem' : '0')};
  overflow-x: ${({ isAllOpen }) => (isAllOpen ? 'auto' : 'hidden')};
  width: 100%;

  p {
    font-family: 'Inter';
    font-size: 14px;
    font-weight: 400;
    white-space: nowrap;

    ${({ isAllOpen }) =>
      !isAllOpen
        ? `text-overflow: ellipsis;
          max-width: calc(400px - 42px - 2rem);
          overflow: hidden;`
        : ''}
  }

  button {
    color: ${({ theme }) => theme.primary};
    background-color: transparent;
    border: 0;
    font-family: 'Inter';
    font-size: 14px;
    font-weight: 400;

    &:hover {
      color: ${({ theme }) => theme.telnyx.red};
      cursor: pointer;
    }
  }
`;

const ShowMore = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  padding: 8px 16px;
  margin-top: 12px;

  border-top: 1px solid ${({ theme }) => theme.line.light};
`;

interface Props {
  documentLoader: DocumentLoader;
  tab: string;
  setTab: React.Dispatch<string>;
}

export const Upload: React.FC<Props> = ({ documentLoader, tab, setTab }) => {
  const queryClient = useQueryClient();

  const [files, setFiles] = useState<File[]>([]);
  const [showAll, setShowAll] = useState(false);
  const [alignment, setAlignment] = useState('file');

  const handleChange = (_: React.MouseEvent<HTMLElement>, newAlignment: string) => {
    if (newAlignment && newAlignment !== alignment) setAlignment(newAlignment);
  };

  const { addDocumentsToQueue } = documentLoader;
  const { enqueueSnackbar } = useSnackbar();
  const { service_id, bucketName } = useParams();

  const bucketQueryKey = ['globalFiles', 'bucket', { service_id, bucket_name: bucketName }];
  const bucket = queryClient.getQueryData<TelnyxBucket>(bucketQueryKey);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    setFiles(acceptedFiles);
  }, []);

  const onFolderDrop = useCallback((acceptedFiles: File[]) => {
    const accepted = acceptedFiles
      .filter((file) => checkFileType(file))
      .filter((v, i, a) => a.findIndex((v2) => v2?.name === v?.name && v.type === v.type) === i);

    const rejected = acceptedFiles.filter((file) => !accepted.some((item) => item.name === file.name));

    if (!accepted.length)
      return enqueueSnackbar('All files were rejected because they do not have a supported format for embedding.', {
        variant: 'error',
        autoHideDuration: 10000,
      });

    if (rejected.length) {
      enqueueSnackbar(
        `${rejected.length} files were rejected because they do not have a supported format for embedding.`,
        {
          variant: 'warning',
          autoHideDuration: 10000,
        }
      );
    }

    setFiles(accepted);
  }, []);

  const maxSize = 20 * 1024 * 1024;

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'text/html': ['.html'],
      'text/plain': ['.txt', '.md'],
      'application/json': ['.json'],
      'text/csv': ['.csv'],
      'audio/*': ['.mp3', '.mpga', '.m4a', '.wav', '.webm'],
      'video/*': ['.mp4', '.mpeg', '.webm'],
    },
    maxSize,
    onDropRejected: (fileRejections) => {
      // This callback can be used to show an error message for rejected files
      fileRejections.forEach(({ file, errors }) => {
        errors.forEach((error) => {
          if (error.code === 'file-too-large') {
            alert(`File ${file.name} is too large. Maximum size is 20MB for audio/video files.`);
          } else if (error.code === 'file-invalid-type') {
            alert(`File ${file.name} is of an invalid type.`);
          }
        });
      });
    },
  });

  const {
    getInputProps: getFolderInputProps,
    getRootProps: getFolderRootProps,
    isDragActive: isFolderDragActive,
  } = useDropzone({
    onDrop: onFolderDrop,
    maxSize,
    useFsAccessApi: false,
    onDropRejected: (fileRejections) => {
      fileRejections.forEach(({ file, errors }) => {
        errors.forEach((error) => {
          if (error.code === 'file-too-large') {
            alert(`File ${file.name} is too large. Maximum size is 20MB for audio/video files.`);
          } else if (error.code === 'file-invalid-type') {
            alert(`File ${file.name} is of an invalid type.`);
          }
        });
      });
    },
  });

  const uploadFilesToQueue = () => {
    if (bucket) {
      addDocumentsToQueue(service_id as string, bucketName as string, bucket.region, files, tab, () => null);
      setFiles([]);
      setShowAll(false);
      setTab('4');
    }
  };

  const checkFileType = (file: File) => {
    const acceptedExtensions = [
      '.pdf',
      '.html',
      '.txt',
      '.md',
      '.json',
      '.csv',
      '.mp3',
      '.mpga',
      '.m4a',
      '.wav',
      '.webm',
      '.mp4',
      '.mpeg',
    ];

    const acceptedFileTypes = {
      'application/pdf': ['.pdf'],
      'text/html': ['.html'],
      'text/plain': ['.txt', '.md'],
      'application/json': ['.json'],
      'text/csv': ['.csv'],
      'audio/*': ['.mp3', '.mpga', '.m4a', '.wav', '.webm'],
      'video/*': ['.mp4', '.mpeg', '.webm'],
    };

    const fileType = file.type;

    // if file is one of accepted file types and one of accepted file extensions, return true else return false
    if (
      fileType in acceptedFileTypes ||
      fileType.includes('text/') ||
      acceptedExtensions.some((ext) => file.name.endsWith(ext))
    ) {
      return true;
    } else {
      return false;
    }
  };

  if (files?.length) {
    return (
      <Container>
        <CustomCard isAllOpen={showAll}>
          <ListContainer isAllOpen={showAll} isPaddingBottom={showAll || files.length > 15}>
            <List dense>
              {(showAll ? files : files.slice(0, 15)).map((file) => (
                <ListItem key={file.name}>
                  <p>{file.name}</p>
                </ListItem>
              ))}
            </List>
            {files.length > 3 && !showAll && (
              <Stack direction='row' alignItems='center' justifyContent='space-between'></Stack>
            )}
            {files.length > 15 && !showAll && (
              <ShowMore>
                <p>...and {files.length - 15} more</p>
                <button onClick={() => setShowAll(true)}>Show more</button>
              </ShowMore>
            )}
          </ListContainer>
          <div className='actions'>
            <Button
              fullWidth
              startIcon={<Clear />}
              onClick={() => {
                setFiles([]);
                setShowAll(false);
              }}
            >
              Cancel
            </Button>
            <Button
              fullWidth
              customColor='primary'
              onClick={uploadFilesToQueue}
              startIcon={<UploadFile sx={{ fontSize: '2ch !important' }} />}
            >
              Upload ({files?.length || 0}) Files
            </Button>
          </div>
        </CustomCard>
      </Container>
    );
  }

  return (
    <Container>
      {alignment === 'folder' ? (
        <FileUpload {...getFolderRootProps()}>
          <Outline isDragActive={isFolderDragActive}>
            <input
              type='file'
              {...getFolderInputProps({
                webkitdirectory: '',
                directory: '',
                mozdirectory: '',
                odirectory: '',
                msdirectory: '',
                multiple: true,
              })}
            />
            <DriveFolderUpload />
            {isFolderDragActive ? <h4>Drop Here to Upload</h4> : <h4>Upload Folder</h4>}
            {<p>{!isFolderDragActive ? 'You can also drag folders here.' : 'txt, md, json, video, audio'}</p>}
          </Outline>
        </FileUpload>
      ) : (
        <FileUpload {...getRootProps()}>
          <Outline isDragActive={isDragActive}>
            <input {...getInputProps()} />
            <UploadFileIcon />
            {isDragActive ? <h4>Drop Here to Upload</h4> : <h4>Upload File</h4>}
            {<p>{!isDragActive ? 'You can also drag files here.' : 'txt, md, json, video, audio'}</p>}
          </Outline>
        </FileUpload>
      )}
      <ToggleButtonGroup color='primary' size='small' value={alignment} exclusive onChange={handleChange} fullWidth>
        <ToggleButton value='file'>File</ToggleButton>
        <ToggleButton value='folder'>Folder</ToggleButton>
      </ToggleButtonGroup>
    </Container>
  );
};
